/**
 * Product page information blocks shared by the classic product page and the Blinkit product page: the ratings
 * summary and the "Product details" attribute grid. Moved verbatim out of ProductDetailsPage.jsx.
 */
import { Star, Info, Ruler, Boxes, Timer, Flame, Tag, Percent, ShoppingBag } from "lucide-react";
import { LOW_STOCK_THRESHOLD } from "../../../components/StockBadge";

export function ReviewBlock({ title, summary, emptyText }) {
    const avg = summary?.avg || 0;
    const count = summary?.count || 0;
    const breakdown = summary?.breakdown || {};
    if (!summary || count === 0) {
        return (
            <div data-testid="pdp-review-empty" className="text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-1">{title}</p>
                {emptyText}
            </div>
        );
    }
    const max = Math.max(1, ...["5", "4", "3", "2", "1"].map((k) => breakdown[k] || 0));
    return (
        <div data-testid="pdp-review-block">
            <div className="flex items-center gap-3">
                <div className="flex flex-col items-start">
                    <span className="text-3xl font-bold leading-none">{avg.toFixed(1)}</span>
                    <span className="inline-flex items-center gap-0.5 mt-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                                key={n}
                                size={14}
                                className={n <= Math.round(avg) ? "fill-amber-500 text-amber-500" : "text-muted-foreground/40"}
                            />
                        ))}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1">{count} {count === 1 ? "rating" : "ratings"}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold mb-2">{title}</p>
                    {["5", "4", "3", "2", "1"].map((k) => {
                        const n = breakdown[k] || 0;
                        const pct = Math.round((n / max) * 100);
                        return (
                            <div key={k} className="flex items-center gap-2 text-xs mb-1">
                                <span className="w-3 text-muted-foreground">{k}</span>
                                <Star size={10} className="fill-amber-500 text-amber-500" />
                                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="w-6 text-right tabular-nums text-muted-foreground">{n}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/**
 * ProductAttributes — customer-facing "details" grid.
 *
 * iter127 — every attribute the merchant may configure in the Admin
 * product editor now surfaces here, so the customer app is no longer
 * a blind spot for values like unit-of-measure, stock, min/max qty,
 * prep time, spice level, GST%, and category tags.
 *
 * Values fall back through: picked-variant → product → nothing. Rows
 * with no value are skipped entirely so the section stays tight and
 * simple items don't render a giant empty table.
 */
export function ProductAttributes({ product, pickedVariant, shop, lowStockThreshold = LOW_STOCK_THRESHOLD }) {
    // Resolve display values with variant-first precedence.
    const uom = pickedVariant?.unit_of_measure || product.unit_of_measure || "";
    const stock = pickedVariant?.stock ?? product.stock;
    const tracksStock = product.track_stock !== false;
    const minQty = product.min_order_qty && product.min_order_qty > 1 ? product.min_order_qty : null;
    const maxQty = product.max_order_qty && product.max_order_qty < 999 ? product.max_order_qty : null;

    // iter134c1 — prep-time is only meaningful for verticals where the
    // shop physically prepares an order after checkout (Food, Meat,
    // Bakery, Pharmacy compounding, Beauty prep, etc). For Electronics
    // / Service Booking / Home Services / Automobile / Real Estate /
    // Finance shops, prep-time is either irrelevant (customer bookings
    // have their own service duration in service_config) or actively
    // misleading. Hide it in those cases.
    //
    // Rules (any → hide):
    //   1. Product is a SERVICE booking (uses service_config.duration_min).
    //   2. Shop's vertical slugs don't intersect the food-like whitelist.
    //
    // Whitelist chosen conservatively so a new/unseen vertical slug
    // defaults to "no prep-time shown" rather than showing a nonsense
    // value. Operators who introduce a new prep-relevant vertical
    // (e.g. bakery, catering) just add it here.
    const PREP_VERTICALS = new Set([
        "food", "meat", "grocery", "pharmacy",
        "bakery", "cafe", "catering", "dairy",
    ]);
    const shopVerticals = shop?.business_category_slugs || [];
    const isPrepVertical = shopVerticals.some((s) => PREP_VERTICALS.has(s));
    const prepApplicable = product.kind !== "SERVICE" && (
        // Legacy shops without vertical slugs on the response fall back
        // to "show prep-time" — matches pre-iter134c1 behaviour so
        // existing food shops with cached data don't lose their label.
        shopVerticals.length === 0 || isPrepVertical
    );
    const prep = prepApplicable && product.prep_time_minutes && product.prep_time_minutes > 0
        ? product.prep_time_minutes
        : null;

    // Spice level + food_type also only meaningful for food-like
    // verticals. Suppress them wholesale on Electronics / Services.
    const foodAttrsApplicable = prepApplicable;
    const spice = foodAttrsApplicable && product.spice_level && product.spice_level !== "NONE"
        ? product.spice_level : null;
    const category = product.category_name || null;
    const gst = product.gst_percent != null ? product.gst_percent : null;
    const foodType = foodAttrsApplicable ? (product.food_type || null) : null;

    // Stock label: hide entirely when the merchant chose not to track,
    // when there's no numeric value, or when the count is "unlimited-y"
    // (>999 is a proxy). Otherwise show either "In stock (N left)" for
    // healthy counts or "Only N left" for low counts (matches the badge
    // logic used on listing cards).
    let stockLabel = null;
    if (tracksStock && typeof stock === "number") {
        if (stock <= 0) stockLabel = "Out of stock";
        else if (stock <= lowStockThreshold) stockLabel = `Only ${stock} left`;
        else if (stock <= 999) stockLabel = `${stock} in stock`;
    }

    const rows = [
        uom && { icon: Ruler, label: "Unit / Size", value: uom, testId: "attr-uom" },
        stockLabel && {
            icon: Boxes,
            label: "Availability",
            value: stockLabel,
            testId: "attr-stock",
            tone: stock <= 0 ? "danger" : stock <= lowStockThreshold ? "warn" : "ok",
        },
        (minQty || maxQty) && {
            icon: ShoppingBag,
            label: "Order quantity",
            value: minQty && maxQty
                ? `${minQty} – ${maxQty}`
                : minQty
                    ? `Min ${minQty}`
                    : `Max ${maxQty}`,
            testId: "attr-qty-range",
        },
        prep && { icon: Timer, label: "Prep time", value: `~${prep} min`, testId: "attr-prep" },
        spice && { icon: Flame, label: "Spice level", value: spice.charAt(0) + spice.slice(1).toLowerCase(), testId: "attr-spice" },
        category && { icon: Tag, label: "Category", value: category, testId: "attr-category" },
        foodType && foodType !== "VEG" && {
            icon: Info, label: "Type", value: foodType.replace("_", " · "), testId: "attr-food-type",
        },
        gst != null && { icon: Percent, label: "GST", value: `${gst}%`, testId: "attr-gst" },
    ].filter(Boolean);

    if (rows.length === 0) return null;

    return (
        <div className="mt-6" data-testid="pdp-attributes">
            <h2 className="text-base font-bold mb-3">Product details</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-2xl border bg-card p-4">
                {rows.map(({ icon: Icon, label, value, testId, tone }) => (
                    <div key={testId} className="flex items-start gap-2" data-testid={testId}>
                        <Icon size={16} className="mt-0.5 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                {label}
                            </dt>
                            <dd className={`text-sm font-semibold truncate ${
                                tone === "danger" ? "text-red-600 dark:text-red-400" :
                                    tone === "warn" ? "text-amber-700 dark:text-amber-400" : ""
                            }`}>
                                {value}
                            </dd>
                        </div>
                    </div>
                ))}
            </dl>
        </div>
    );
}
